import React, { createContext, useContext, useRef } from 'react';

export interface CameraControlsContextValue {
  moveForward: (isMoving: boolean) => void;
  onMoveForward: (callback: (isMoving: boolean) => void) => () => void;
  moveBackward: (isMoving: boolean) => void;
  onMoveBackward: (callback: (isMoving: boolean) => void) => () => void;
  turnLeft: (isTurning: boolean) => void;
  onTurnLeft: (callback: (isTurning: boolean) => void) => () => void;
  turnRight: (isTurning: boolean) => void;
  onTurnRight: (callback: (isTurning: boolean) => void) => () => void;
}

const CameraControlsContext = createContext<CameraControlsContextValue | undefined>(undefined);

export const CameraControlsProvider = ({ children }: any) => {
  const moveForwardCallbacks = useRef<((isMoving: boolean) => void)[]>([]);
  const moveBackwardCallbacks = useRef<((isMoving: boolean) => void)[]>([]);
  const turnLeftCallbacks = useRef<((isTurning: boolean) => void)[]>([]);
  const turnRightCallbacks = useRef<((isTurning: boolean) => void)[]>([]);

  const triggerCallbacks = (
    callbacks: React.MutableRefObject<((isActive: boolean) => void)[]>,
    isActive: boolean
  ) => {
    callbacks.current.forEach(callback => callback(isActive));
  };

  const subscribe = (
    callbacks: React.MutableRefObject<((isMoving: boolean) => void)[]>,
    callback: (isMoving: boolean) => void
  ) => {
    callbacks.current.push(callback);
    return () => {
      const index = callbacks.current.indexOf(callback);
      if (index > -1) {
        callbacks.current.splice(index, 1);
      }
    };
  };

  const value: CameraControlsContextValue = {
    moveForward: (isMoving: boolean) => triggerCallbacks(moveForwardCallbacks, isMoving),
    onMoveForward: (callback: (isMoving: boolean) => void) => subscribe(moveForwardCallbacks, callback),
    moveBackward: (isMoving: boolean) => triggerCallbacks(moveBackwardCallbacks, isMoving),
    onMoveBackward: (callback: (isMoving: boolean) => void) => subscribe(moveBackwardCallbacks, callback),
    turnLeft: (isTurning: boolean) => triggerCallbacks(turnLeftCallbacks, isTurning),
    onTurnLeft: (callback: (isTurning: boolean) => void) => subscribe(turnLeftCallbacks, callback),
    turnRight: (isTurning: boolean) => triggerCallbacks(turnRightCallbacks, isTurning),
    onTurnRight: (callback: (isTurning: boolean) => void) => subscribe(turnRightCallbacks, callback),
  };

  return (
    <CameraControlsContext.Provider value={value}>
      {children}
    </CameraControlsContext.Provider>
  );
};

export const useCameraControls = () => {
  return useContext(CameraControlsContext);
};
